<template>
  <div class="survey-list">
    <!-- Page breadcrumb -->
    <div class="page-breadcrumb border-bottom">
      <div class="row">
        <div class="col-lg-3 col-md-4 col-xs-12 align-self-center">
          <h5 class="mb-0 font-medium text-uppercase">
            Survey Management
            <span class="badge badge-primary">{{ allSurveys.length }}</span>
          </h5>
        </div>
        <div class="col-lg-9 col-md-8 col-xs-12 align-self-center">
          <button
            class="float-right ml-3 text-white btn btn-primary d-none d-md-block"
            @click="openCreateNewSurveyModal()"
          >
            <i class="fas fa-plus"></i> Create new Survey
          </button>
          <nav aria-label="breadcrumb" class="float-left mt-2 float-md-right">
            <ol class="p-0 mb-0 breadcrumb justify-content-end">
              <li class="breadcrumb-item">
                <a>Home</a>
              </li>
              <li class="breadcrumb-item active" aria-current="page">
                Survey Management
              </li>
            </ol>
          </nav>
        </div>
      </div>
    </div>

    <!-- Survey list -->
    <div class="page-content container-fluid">
      <div class="row">
        <div class="col-12">
          <div class="card">
            <div class="card-body">
              <div class="row">
                <div class="form-group col-4">
                  <div class="input-group">
                    <span class="input-group-btn">
                      <button type="button" class="btn waves-effect waves-light btn-info" @click="searchSurvey()">
                        <i class="fa fa-search"></i>
                      </button>
                    </span>
                    <input type="text" class="form-control ml-2" placeholder="Search by AhaSlides ID, Crazy Egg ID, Name" @keydown.enter.prevent="searchSurvey()" v-model="surveySearchKey"/>
                  </div>
                </div>
                <!-- Help button -->
                <div>
                  <button type="button" class="btn btn-warning" @click="openGuideInNewTab()">
                    How to set up Crazy egg survey
                  </button>
                </div>
              </div>
              <div class="table-responsive">
                <div class="text-center">
                  <i class="fa fa-spinner fa-spin" v-if="isGettingListSurveys"></i>
                </div>
                <table class="table">
                  <thead>
                    <tr>
                      <th>AhaSlides Survey ID</th>
                      <th class="table-heading__created-at" @click="sortSurveysByStatus()">
                        Status
                        <i class="fa fa-sort-up fa-fw" v-if="sortByStatusInactive"></i>
                        <i class="fa fa-sort-down fa-fw" v-else></i>
                      </th>
                      <th>Name</th>
                      <th>Survey code</th>
                      <th class="table-heading__created-at" @click="sortSurveysByCreatedAt()">
                        Created at
                        <i class="fa fa-sort-up fa-fw" v-if="sortByCreatedAtAscending"></i>
                        <i class="fa fa-sort-down fa-fw" v-else></i>
                      </th>
                      <th>Crazy Egg Survey ID</th>
                      <th>Delay time</th>
                      <th>Edit survey</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(survey) in isFilteringSurveys ? allSurveysFiltered : allSurveys" :key="survey.id">
                      <td>{{ survey.id }}</td>
                      <td>
                        <toggle-button
                          :sync="isSurveyStatusChanged"
                          :value="survey.status === SurveyStatus.ACTIVE ? true: false"
                          :width="35"
                          :height="20"
                          class="mb-2"
                          :color="{
                            checked: '#33C173',
                            unchecked: '#D3D7E1'
                          }"
                          @change="onChangeSurveyStatus(survey)"
                        />
                      </td>
                      <td>{{ survey.surveyName }}</td>
                      <td class="text-uppercase">{{ survey.surveyCode }}</td>
                      <td>{{ survey.createdAt | dateTime }}</td>
                      <td>{{ survey.crazyEggSurveyId }}</td>
                      <td>{{ survey.delayTime }} <span v-if="survey.delayTime >= 0">s</span></td>
                      <td class="list__edit-survey" @click="openEditSurveyModal(survey)">
                        <i class="fa fa-pencil-alt fa-fw"></i>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <!-- Pagination -->
                <div class="overflow-auto">
                  <!-- <b-pagination
                    v-model="page"
                    :total-rows="count"
                    :per-page="size"
                  ></b-pagination> -->
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <CreateNewSurveyModal
      v-if="isCreateNewSurveyModalVisible"
      @closeCreateNewSurveyModal="isCreateNewSurveyModalVisible = false"
      @createNewSurvey="createNewSurvey"
      :allSurveys="allSurveys"
      @openTargetUserWarningModal="isTargetUserWarningModalVisible = true"
    />

    <EditSurveyModal
      v-if="isEditSurveyModalVisible"
      :data="surveyDataToEdit"
      @closeEditSurveyModal="closeEditSurveyModal"
      @editSurvey="handleEditSurveyData"
      :allSurveys="allSurveys"
      @openTargetUserWarningModal="isTargetUserWarningModalVisible = true"
    />

    <TargetUserWarningModal
      v-if="isTargetUserWarningModalVisible"
      @closeTargetUserWarningModal="isTargetUserWarningModalVisible = false"
    />
  </div>
</template>

<script>
import CreateNewSurveyModal from './CreateNewSurveyModal.vue'
import EditSurveyModal from './EditSurveyModal.vue'
import TargetUserWarningModal from './TargetUserWarningModal.vue'
import SurveyApi from '@/api/survey.js'
import SurveyStatus from '@/constants/SurveyStatus'
import Utils from '@/utils.js'

export default {
  components: {
    CreateNewSurveyModal,
    EditSurveyModal,
    TargetUserWarningModal,
  },
  data() {
    return {
      allSurveys: [],
      allSurveysFiltered: [],
      isCreateNewSurveyModalVisible: false,
      isEditSurveyModalVisible: false,
      isTargetUserWarningModalVisible: false,
      surveyDataToEdit: {},
      sortByCreatedAtAscending: false,
      sortByStatusInactive: true,
      surveySearchKey: '',
      isFilteringSurveys: false,
      SurveyStatus,
      isSurveyStatusChanged: false,
      isGettingListSurveys: false,
    }
  },
  computed: {
    hasSurveySearchKey() {
      return this.surveySearchKey && this.surveySearchKey.length
    },
  },
  mounted() {
    this.getListSurveys()
  },
  methods: {
    async getListSurveys() {
      try {
        this.isGettingListSurveys = true
        const surveys = await SurveyApi.listSurveys()
        this.allSurveys = surveys.sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt))
        this.isGettingListSurveys = false
      } catch (error) {
        Utils.showToastError('Cannot get list of surveys. Check your connection and try again.')
      }
    },
    openCreateNewSurveyModal() {
      this.isCreateNewSurveyModalVisible = true
    },
    openEditSurveyModal(survey) {
      this.surveyDataToEdit = survey
      this.isEditSurveyModalVisible = true
    },
    closeEditSurveyModal() {
      this.isEditSurveyModalVisible = false
    },
    async createNewSurvey(survey) {
      try {
        this.isGettingListSurveys = true
        const newSurvey = await SurveyApi.createSurvey(survey)
        this.allSurveys.push(newSurvey.survey)
        this.isGettingListSurveys = false
        Utils.showToastSuccess('Create new survey successfully!')
      } catch (error) {
        Utils.showToastError('Cannot create new survey. Check your connection and try again.')
      }
    },
    async handleEditSurveyData(surveyId, editedSurvey) {
      try {
        await SurveyApi.patchSurvey(surveyId, editedSurvey)
        this._updateEditedSurveyToAllSurveys(surveyId, editedSurvey)
        Utils.showToastSuccess('Edit survey successfully!')
      } catch (error) {
        Utils.showToastError('Cannot edit survey. Check your connection and try again.')
      } finally {
        this.closeEditSurveyModal()
      }
    },
    async onChangeSurveyStatus(editedSurvey) {
      try {
        this.isSurveyStatusChanged = false
        const payload = {
          status: editedSurvey.status === SurveyStatus.ACTIVE ? SurveyStatus.INACTIVE : SurveyStatus.ACTIVE,
        }
        this._updateEditedSurveyToAllSurveys(editedSurvey.id, payload)
        this.isSurveyStatusChanged = true
        await SurveyApi.patchSurvey(editedSurvey.id, payload)
      } catch (error) {
        this.isSurveyStatusChanged = false
        //
      }
    },
    _updateEditedSurveyToAllSurveys(surveyId, editedSurvey) {
      const updateFunction = survey => (survey.id === surveyId ? { ...survey, ...editedSurvey } : survey)
      this.allSurveysFiltered = this.allSurveysFiltered.map(updateFunction)
      this.allSurveys = this.allSurveys.map(updateFunction)
      if (editedSurvey.status) {
        this.isSurveyStatusChanged = true
      }
    },
    sortSurveysByCreatedAt() {
      this.sortByCreatedAtAscending = !this.sortByCreatedAtAscending
      const surveys = this.isFilteringSurveys ? this.allSurveysFiltered : this.allSurveys
      const sortingFunction = this.sortByCreatedAtAscending
          ? (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
          : (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      surveys.sort(sortingFunction)
    },
    sortSurveysByStatus() {
      this.sortByStatusInactive = !this.sortByStatusInactive
      const surveys = this.isFilteringSurveys ? this.allSurveysFiltered : this.allSurveys
      const sortingFunction = this.sortByStatusInactive
          ? (a, b) => (a.status === SurveyStatus.INACTIVE) - (b.status === SurveyStatus.INACTIVE)
          : (a, b) => (b.status === SurveyStatus.INACTIVE) - (a.status === SurveyStatus.INACTIVE)
      surveys.sort(sortingFunction)
    },
    searchSurvey() {
      this.isFilteringSurveys = true
      if (!this.hasSurveySearchKey) {
        this.allSurveysFiltered = this.allSurveys
        this.isFilteringSurveys = false
        return
      }

      // Search by AhaSlides ID, Crazy Egg ID, Name
      const searchKey = this.surveySearchKey.toLowerCase();
      const result = this.allSurveys.filter((survey) => {
        return (
            survey.surveyName.toLowerCase().includes(searchKey) ||
            survey.id == searchKey ||
            survey.crazyEggSurveyId == searchKey
        )
      })
      this.allSurveysFiltered = result
    },

    openGuideInNewTab() {
      window.open('https://ahaslides.atlassian.net/wiki/spaces/ATS/pages/616661109/How+to+set+up+Crazy+Egg+survey+on+55broadway', '_blank')
    },
  }
}
</script>

<style lang="scss" scoped>
.survey-list {
  font-family: "Nunito", sans-serif !important;
}
.table-heading__created-at {
  cursor: pointer;
}
.list__edit-survey {
  &:hover {
    color: #348EF6; // primary 90
    cursor: pointer;
  }
}
.button-add-page {
  font-size: 13px;
}
.flex {
  display: flex;
}
.items-center {
  align-items: center;
}
</style>